import EventClient from "@2u-landing-pages/utils/event-client";
import { handlePIIFields } from "@2u-landing-pages/utils/formatting";
import { useEffect, useRef, useState } from "react";
import {
  checkSubmitError,
  createAutofillTracker,
  handleSubmitEvents,
} from "../LeadForm/useFormEvents";

type Steps = {
  current: number | null;
  total: number | null;
};

declare global {
  interface Window {
    cwr: any;
  }
}

export const useFormEvents = (formId: string, leadFormType: "TY" | "LP", location?: string) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [steps, setSteps] = useState<Steps>({
    current: null,
    total: null,
  });

  const getLeadFormStep = () => {
    if (!formContainerRef.current) return;

    const label = formContainerRef.current.querySelector(".progress__label");

    if (!label) {
      setSteps({
        current: 1,
        total: 1,
      });

      return {
        current: 1,
        total: 1,
      };
    }

    const stepString = label?.textContent;
    const regex = /(\d+) of (\d+)/;
    const match = stepString?.match(regex) || [];
    const updatedSteps = {
      current: parseInt(match[1], 10) || 1,
      total: parseInt(match[2], 10) || 1,
    };

    setSteps(updatedSteps);

    return updatedSteps;
  };

  useEffect(() => {
    if (!formContainerRef.current) return;

    const root = formContainerRef.current;
    const autofillTracker = createAutofillTracker();

    const handleBlurEvent = (event: FocusEvent) => {
      const originalTarget = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (
        originalTarget?.tagName === "INPUT" &&
        ["text", "email", "tel"].includes(originalTarget.type)
      ) {
        // Check autofill on the live DOM node before PII sanitization clones it
        const autofilled = autofillTracker.isAutofilled(originalTarget);
        const target = handlePIIFields(originalTarget);
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled,
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleChangeEvent = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (
        target?.tagName === "SELECT" ||
        (target?.tagName === "INPUT" && !["text", "email", "tel", "password"].includes(target.type)) ||
        target?.type === "radio" ||
        target?.type === "checkbox"
      ) {
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled: autofillTracker.isAutofilled(target),
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleClickEvent = (event: MouseEvent) => {
      const target = event.target as HTMLInputElement | HTMLAnchorElement;

      if (
        target?.classList.contains("lead-form-next-step") ||
        (target?.classList.contains("button") && target?.closest(".form-get-info")) ||
        (target?.classList.contains("form-button") && target?.closest(".multi-step-form"))
      ) {
        const { innerText } = target;

        // Capture step BEFORE click for event tracking
        const { current: stepBeforeClick, total: totalSteps } = getLeadFormStep() || {};
        // Form is complete when clicking from the last step (submit button)
        const complete = stepBeforeClick === totalSteps;

        // wait for the form to update before checking for errors
        setTimeout(() => {
          if (!formContainerRef.current) return;

          const inputErrors = Array.from(
            formContainerRef.current.querySelectorAll(
              "input.error, select.error, .form-group.is--invalid, .form-field-group.invalid"
            )
          );

          if (inputErrors.length) {
            const errorMessages = formContainerRef.current.querySelectorAll(".is--invalid");
            const errorMessage =
              Array.from(errorMessages)
                .map((el) => el.textContent)
                .join(", ") ?? "";

            EventClient.formErrored({
              errorMessage,
              formContext: {
                formType: `${leadFormType === "TY" ? "TYP" : "LP"}${location ? ` - ${location}` : ""}`,
                formId,
              },
              stepContext: {
                stepNumber: stepBeforeClick as number,
              },
            });

            return;
          }

          EventClient.elementClicked({
            webElement: {
              htmlId: formId,
              text: innerText || "",
              position: `Step: ${stepBeforeClick}`,
              location: `${leadFormType === "TY" ? "TYP" : "LP"}${location ? ` - ${location}` : ""}`,
            },
          });

          if (leadFormType === "LP") {
            // Use optimistic approach for LP (submit doesn't require server-side validation)
            handleSubmitEvents(formId, stepBeforeClick, totalSteps, leadFormType, location);
          }

          if (complete) {
            if (leadFormType === "TY") {
              setTimeout(() => {
                const submitError = checkSubmitError(formContainerRef, formId, stepBeforeClick);

                if (!submitError) {
                  handleSubmitEvents(formId, stepBeforeClick, totalSteps, leadFormType, location);
                }
              }, 1000);
            }
          }
        });
      }
    };

    root.addEventListener("animationstart", autofillTracker.onAnimationStart, true);
    root.addEventListener("blur", handleBlurEvent, true);
    root.addEventListener("change", handleChangeEvent, true);
    root.addEventListener("click", handleClickEvent, true);

    return () => {
      root.removeEventListener("animationstart", autofillTracker.onAnimationStart, true);
      root.removeEventListener("blur", handleBlurEvent, true);
      root.removeEventListener("change", handleChangeEvent, true);
      root.removeEventListener("click", handleClickEvent, true);
    };
  }, []);

  return {
    formContainerRef,
    steps,
  };
};
